import { MessageCircle } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import useConversationStore from '@/stores/Conversation';

import { useCreateConversation } from '../api/createConversation';
import { useConversation } from '../api/getConversation';
import { useMarkAsRead } from '../api/markAsRead';
import { useChatSocket } from '../hooks/useChatSocket';
import { Conversation } from '../types';

import { DayGroupComponent } from './DayGroup';
import { MessageInput } from './MessageInput';

export const ChatConversation = ({
  conversation,
}: {
  conversation: Conversation;
}) => {
  const conversationId = conversation.id && 'conversation_' + conversation.id;
  const scrollRef = useRef<HTMLDivElement>(null);
  const {
    feedback,
    nameRef,
    useridRef,
    imageRef,
    sendMessageToServer,
    emitTypingStatus,
    emitNewUser,
  } = useChatSocket(conversationId);
  const useConversationQuery = useConversation({
    conversationId,
    queryConfig: {
      enabled: !!conversationId,
    },
  });
  const createConversationQuery = useCreateConversation();
  const { setSelectedConversation } = useConversationStore();

  const { mutate: markConversationAsRead } = useMarkAsRead();

  useEffect(() => {
    if (conversationId) {
      markConversationAsRead({ conversationId });
    }
  }, [conversationId, markConversationAsRead]);

  // Scroll to bottom when new messages are added
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [useConversationQuery.data]);

  if (useConversationQuery.isLoading) {
    return <Spinner className="m-auto" />;
  }

  if (useConversationQuery.error) {
    return (
      <Alert
        variant="destructive"
        className="mx-auto my-4 w-11/12 px-2 lg:mt-8"
      >
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>Failed to load conversation</AlertDescription>
      </Alert>
    );
  }

  const handleSendMessage = (message: string) => {
    if (createConversationQuery.isPending) {
      return;
    }

    if (conversation.draft) {
      createConversationQuery.mutate(
        { data: conversation },
        {
          onSuccess: (newConversation) => {
            setSelectedConversation(newConversation);
            const newConversationId = 'conversation_' + newConversation.id;
            emitNewUser(newConversationId).then(() => {
              sendMessageToServer(message, newConversationId);
            });
          },
        },
      );
    } else {
      sendMessageToServer(message, conversationId);
    }
  };

  return (
    <>
      {createConversationQuery.isPending ? (
        <div className="flex h-full min-h-96 flex-col items-center justify-center p-5 text-center">
          <MessageCircle size={'8rem'} color="gray" />
          <div className="mx-auto max-w-96">
            <p className="mt-2 font-medium">No messages here yet</p>
            <p className="mb-5 flex items-center gap-x-2 text-sm text-muted-foreground">
              <Spinner size="sm" />
              Creating conversation...
            </p>
          </div>
        </div>
      ) : conversation.draft || useConversationQuery.data?.length === 0 ? (
        <div className="flex h-full min-h-96 flex-col items-center justify-center p-5 text-center">
          <MessageCircle size={'8rem'} color="gray" />
          <div className="mx-auto max-w-96">
            <p className="mt-2 font-medium">No messages here yet</p>
            <p className="mb-5 text-sm text-muted-foreground">
              Send a message and start a conversation
            </p>
          </div>
        </div>
      ) : (
        <div
          ref={scrollRef}
          className="h-full overflow-y-auto overflow-x-hidden"
        >
          <div className="space-y-5 px-5 py-5">
            {useConversationQuery.data?.map((group, index) => (
              <DayGroupComponent key={index} group={group} />
            ))}
          </div>
        </div>
      )}
      <MessageInput
        conversationId={conversationId}
        nameRef={nameRef}
        useridRef={useridRef}
        imageRef={imageRef}
        onSendMessage={handleSendMessage}
        emitTypingStatus={emitTypingStatus}
        feedback={feedback}
      />
    </>
  );
};
